import { useState, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { BackButton } from '@/components/ui/app/back-button';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import FormProcedureCreate, { FORM_PROCEDURE_CREATE_ID } from './_partials/forms/FormProcedureCreate';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { frontendToBackendFieldType } from '@/lib/utils/procedure-field-types';
import { useTranslation } from 'react-i18next';

export default function ProcedureCreate() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [resetKey, setResetKey] = useState(0);

    // Paramètres de type et de retour (ex: ?type=admin&returnTo=/settings/superadmin/procedures)
    const procedureType = searchParams.get('type') || 'standard';
    const returnTo = searchParams.get('returnTo');
    const isAdminType = procedureType === 'admin';

    // Récupérer les données sauvegardées si on revient de l'aperçu
    const savedFormData = location.state?.procedureData || null;
    const [formData, setFormData] = useState(savedFormData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { invalidateCache } = useApiCache();

    const handleSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            // Préparer le payload pour l'API
            const payload = {
                name: data.name,
                description: data.description || null,
                color: data.color,
                is_locked: data.is_locked ?? false,
                ...(isAdminType && { type: 'admin' }),
                // [DISABLED] include_photo_certificates: data.include_photo_certificates ?? false,
                selected_companies: data.selected_companies ?? [],
                configuration: {
                    sections: data.sections.map((section) => ({
                        id: section.id,
                        name: section.name || '',
                        description: section.description || '',
                        fields: section.fields.map((field) => ({
                            id: field.id,
                            type: frontendToBackendFieldType(field.type),
                            label: field.label || '',
                            required: field.required || false,
                            ...(field.options && { options: field.options.filter((opt) => opt.trim() !== '') }),
                            ...(field.instructions && { instructions: field.instructions }),
                        })),
                    })),
                },
            };

            const response = await horodatyApi.procedureTemplates.create(payload);

            if (response.data.success) {
                toast.success(isAdminType ? t('procedures:procedureTypeCreeeAvecSucces') : t('procedures:procedureCreeeAvecSucces'));
                invalidateCache('procedure-templates');
                if (isAdminType) invalidateCache('admin-procedure-templates');
                navigate(returnTo || '/procedures');
            } else {
                toast.error(response.data.message || t('procedures:erreurLorsDeLaCreation'));
            }
        } catch (err) {
            console.error('Error creating procedure:', err);
            if (err.response?.status === 422) {
                // Erreur de validation
                const errors = err.response.data.errors;
                const firstError = Object.values(errors || {})[0];
                toast.error(Array.isArray(firstError) ? firstError[0] : t('procedures:donneesInvalides'));
            } else if (err.response?.status === 403) {
                toast.error(t('procedures:vousNAvezPasLesDroitsPourCreer'));
            } else {
                toast.error(t('procedures:erreurLorsDeLaCreationDeLaProcedure'));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFormDataChange = useCallback((data) => {
        setFormData(data);
    }, []);

    return (
        <div className="bg-muted relative size-full overflow-hidden flex flex-col">
            <div className="flex flex-col h-full">
                {/* Header */}
                <PageHeader title={isAdminType ? t('procedures:nouvelleProcedureType') : t('procedures:nouvelleProcedure')}>
                    <BackButton />
                </PageHeader>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto scrollbar-hide overscroll-contain pb-[calc(80px+64px+var(--safe-area-inset-bottom))] md:pb-[100px]">
                    <div className="md:max-w-[62.5%] md:min-w-[728px] mx-auto">
                        <FormProcedureCreate
                            onSubmit={handleSubmit}
                            onReset={resetKey}
                            onFormDataChange={handleFormDataChange}
                            initialData={savedFormData}
                        />
                    </div>
                </div>

                {/* Fixed Footer */}
                <div className="fixed bottom-0 left-0 right-0 bg-background px-[20px] pt-[16px] pb-[calc(16px+env(safe-area-inset-bottom,0px))] shadow-[0_-2px_10px_rgba(0,0,0,0.1)] z-30">
                    <div className="flex gap-[12px] md:max-w-[62.5%] md:min-w-[728px] mx-auto">
                        <SubmitButton
                            isSubmitting={isSubmitting}
                            loadingText={t('procedures:creation')}
                            size="xxl"
                            form={FORM_PROCEDURE_CREATE_ID}
                            className="flex-1"
                        >
                            {isAdminType ? t('procedures:creerLaProcedureType') : t('procedures:creerLaProcedure')}
                        </SubmitButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
